import { Container, Bounds } from 'pixi.js';
import type { HTMLText } from 'pixi.js';
import { Signal } from 'typed-signals';
import { MathUtils } from '@pixi/spine-pixi';
import type { Popup } from '../../../popups/Popup';
import type { CollectionEventConfig } from '../types';
import { CollectEventPopupLayout } from '../../../../layouts/collectEvent/CollectEventPopupLayout';
import { CollectionEventImageView } from './CollectionEventImageView';
import { CollectionEventProgressStaticProgressBarView } from './CollectionEventProgressStaticProgressBarView';
import { playLayoutAnimation } from '../../../libs/layouts/layoutAnimations/playLayoutAnimation';
import { processColorTags } from '../../../libs/layouts/layoutPostProcessors/layoutColorTagsSPL';
import { CollectionEventRewardTrackView } from './CollectionEventRewardTrackView';
import ScrollBehaviour, { Direction } from '../../../utils/ScrollBehaviour';
import { getCollectionEventRewardProgress } from '../model/collectionEventUtils';
import { skipToTheEndOfAnimation } from '../../../libs/layouts/layoutAnimations/skipToTheEndOfAnimation';

export class CollectionEventPopup extends Container implements Popup {
  public static readonly requiredLayouts = [CollectEventPopupLayout];

  public readonly onHelpClicked = new Signal();

  public readonly onCloseClicked = new Signal();

  public readonly onCloseHelpClicked = new Signal();

  private readonly layout = new CollectEventPopupLayout();

  private rewardTrackView: CollectionEventRewardTrackView | null = null;

  public readonly root = this.layout.root;

  private readonly scrollMinMax = { min: 0, max: 0 };

  private readonly scrollViewBounds = new Bounds(
    -Infinity,
    this.layout.rewardsContainer.y + this.layout.contentSlot.y,
    Infinity,
    0,
  );

  constructor(
    private readonly eventInstance: CollectionEventConfig,
    language: string | null,
  ) {
    super();
    this.init(language ?? 'en_US');
  }

  private init(language: string): void {
    const {
      imageSlot,
      root,
      contentSlot,
      eventTitle,
      progressBarSlot,
      helpButton,
      closeButton,
      contentSlotMask,
      letsGoButton,
      instructionText,
      scrollInput,
    } = this.layout;

    new CollectionEventImageView(this.eventInstance.visuals).props({
      parent: imageSlot,
    });

    contentSlot.mask = contentSlotMask;

    root.props({
      parent: this,
    });

    closeButton.on('pointertap', () => {
      this.onCloseClicked.emit();
    });

    helpButton.on('pointertap', () => {
      this.onHelpClicked.emit();
    });

    letsGoButton.root.on('pointertap', () => {
      this.onCloseHelpClicked.emit();
    });

    new CollectionEventProgressStaticProgressBarView(this.eventInstance).props({
      parent: progressBarSlot,
    });

    eventTitle.text = this.eventInstance.title[language];
    instructionText.text = this.eventInstance.description.text[language];
    processColorTags(instructionText as unknown as HTMLText, this.eventInstance.description.colorTags);

    this.rewardTrackView = new CollectionEventRewardTrackView(this.eventInstance).props({
      parent: contentSlot,
    });

    void playLayoutAnimation(this.layout, 'Idle', true);

    void playLayoutAnimation(this.layout, 'Appear', false);

    this.updateScrollViewBounds();

    this.scrollMinMax.max = contentSlot.y;
    this.scrollMinMax.min = contentSlot.y - this.rewardTrackView.totalHeight + this.scrollViewBounds.height;

    this.centerOnNode(
      getCollectionEventRewardProgress(this.eventInstance, this.eventInstance.tokensCollected).rewardIndex,
    );

    new ScrollBehaviour({
      verticalScroll: this.scrollMinMax,
      inputLayer: scrollInput,
      renderer: this.pixi.renderer,
      target: contentSlot,
      rubberBanding: true,
      wheelDirection: Direction.Vertical,
    }).onTargetPositionChanged.connect(() => {
      this.rewardTrackView?.updateRecyclableViews(this.scrollViewBounds);
    });

    this.rewardTrackView?.updateRecyclableViews(this.scrollViewBounds);
  }

  private centerOnNode(index: number): void {
    if (!this.rewardTrackView) return;
    this.layout.contentSlot.y = MathUtils.clamp(
      -this.rewardTrackView.getNodeY(index) + this.scrollViewBounds.height * 0.7,
      this.scrollMinMax.min,
      this.scrollMinMax.max,
    );
  }

  public async switchToHelpView(immediate = false): Promise<void> {
    if (immediate) {
      skipToTheEndOfAnimation(this.layout, 'SwitchToHelp');
      return;
    }
    await playLayoutAnimation(this.layout, 'SwitchToHelp');
  }

  public async switchToRewardView(): Promise<void> {
    await playLayoutAnimation(this.layout, 'SwitchToReward');
  }

  private updateScrollViewBounds(): void {
    this.scrollViewBounds.height = this.pixi.renderer.height - this.scrollViewBounds.y;
  }

  public resize(): void {
    const { height } = this.root.pixi.renderer;
    this.root.y = Math.ceil(height * -0.5);
    this.updateScrollViewBounds();
    this.scrollMinMax.min =
      this.scrollMinMax.max - (this.rewardTrackView?.totalHeight ?? 0) + this.scrollViewBounds.height;
  }
}
